import { useMemo } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import log from 'loglevel'
import {
  MeDocument,
  MeQuery,
  UserPreferencesUpdateMutation,
} from '@generatedTypes/graphql'
import type {
  ApolloCache,
  DefaultContext,
  FetchResult,
  MutationFunctionOptions,
  OperationVariables,
} from '@apollo/client'
import type User from '@models/User'
import AnonymousUser from '@models/AnonymousUser'
import AuthProviderUser from '@models/AuthProviderUser'
import BaseUser from '@models/BaseUser'
import FirmUser from '@models/FirmUser'
import SubscriberUser from '@models/SubscriberUser'
import { type Auth } from '@utils/useFirebaseAuth'

const userPreferencesUpdateMutation = gql(/* GraphQL */ `
  mutation UserPreferencesUpdate($userKey: Key!, $data: JSON!) {
    userPreferencesUpdate(userKey: $userKey, data: $data) {
      ... on UpdatePreferencesMutationResponse {
        code
        success
        message
        userPreferences
      }
      ... on MutationError {
        code
        success
        message
        userErrors {
          code
          fieldErrors {
            name
            errors {
              code
              message
            }
          }
        }
      }
    }
  }
`)

export const defaultUser = new AnonymousUser()

export interface CurrentUser {
  loading: boolean
  user: User
  updateUserPreferences: (
    mutationOpts:
      | MutationFunctionOptions<
          UserPreferencesUpdateMutation,
          OperationVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<UserPreferencesUpdateMutation>>
  preferencesUpdating: boolean
  preferencesError: any
}

interface Props {
  auth: Auth
  onError?: (error: Error) => void
}

export const useUserProfile = ({ auth }: Props): CurrentUser => {
  const {
    data,
    loading: userLoading,
    error,
  } = useQuery<MeQuery>(MeDocument, {
    skip: !auth.isAuthenticated,
  })

  const [
    updateUserPreferences,
    { loading: preferencesUpdating, error: preferencesError },
  ] = useMutation<UserPreferencesUpdateMutation>(
    userPreferencesUpdateMutation,
    {
      update(cache, { data }) {
        if (
          data?.userPreferencesUpdate.__typename ===
          'UpdatePreferencesMutationResponse'
        ) {
          const newPreferences = data?.userPreferencesUpdate?.userPreferences

          const existingMe = cache.readQuery<MeQuery>({ query: MeDocument })
          const user = existingMe?.me
          if (!user) return

          const userId = cache.identify(user)

          cache.modify({
            id: userId,
            fields: {
              preferences() {
                return newPreferences
              },
            },
          })
        }
      },
    },
  )

  const loading = auth.authLoading || userLoading

  if (error != null) {
    log.error('Error fetching user profile', error)
  }

  return useMemo(() => {
    const baseReturn = {
      loading,
      updateUserPreferences,
      preferencesUpdating,
      preferencesError,
    }

    if (loading || data == null) {
      return {
        user: defaultUser,
        ...baseReturn,
      }
    }

    if (data?.me.__typename === 'BaseUser') {
      return {
        user: new BaseUser({
          photoURL: auth.user?.photoURL,
          ...data.me,
        }),
        ...baseReturn,
      }
    } else if (data?.me.__typename === 'FirmUser') {
      return {
        user: new FirmUser({
          photoURL: auth.user?.photoURL,
          ...data.me,
        }),
        ...baseReturn,
      }
    } else if (data?.me.__typename === 'SubscriberUser') {
      return {
        user: new SubscriberUser({
          photoURL: auth.user?.photoURL,
          ...data.me,
        }),
        ...baseReturn,
      }
    } else {
      if (auth.isAuthenticated && auth.user != null) {
        return { user: new AuthProviderUser(auth.user), ...baseReturn }
      }
    }

    return { user: defaultUser, ...baseReturn }
  }, [
    loading,
    data,
    auth.user,
    auth.isAuthenticated,
    updateUserPreferences,
    preferencesUpdating,
    preferencesError,
  ])
}

export default useUserProfile
